import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LinkifyTextarea } from '@/components/shared/components/Linkify/LinkifyTextarea';

import { MentionDropdown } from './MentionDropdown';
import { MentionContainer } from './MentionTextarea.StyledComponents';
import { MentionSuggestion, MentionTextareaProps } from './MentionTextarea.types';
import { filterSuggestions, insertMention } from './MentionTextarea.utils';
import { setCaretOffset, useMentionTrigger } from './useMentionTrigger';

/**
 * Wraps {@link LinkifyTextarea} with Slack/ClickUp-style `@` mention typeahead.
 * Typing `@` at a word boundary opens a dropdown of TFIN email suggestions;
 * selecting one inserts the plain email into the text. The output is just text
 * — no markup — so the ORIG-747 backend regex matches it the same as a manually
 * typed address.
 *
 * Wrapping (rather than modifying) LinkifyTextarea keeps URL highlighting and
 * paste handling intact; we reach its inner contentEditable through the
 * container ref to read/restore the caret.
 */
export const MentionTextarea = ({
    id,
    value,
    onChange,
    placeholder,
    hasError,
    disabled,
    spellCheck,
    suggestions,
    'data-testid': dataTestId,
}: MentionTextareaProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { triggerState, detect, close, getEditable } = useMentionTrigger(containerRef);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const filtered = useMemo<MentionSuggestion[]>(
        () => (triggerState.isActive ? filterSuggestions(suggestions, triggerState.query) : []),
        [triggerState.isActive, triggerState.query, suggestions],
    );

    const isOpen = triggerState.isActive && Boolean(triggerState.caretRect);

    // Reset the highlighted row whenever the filtered set changes so the
    // selection never points past the end of the list.
    useEffect(() => {
        setSelectedIndex(0);
    }, [triggerState.query, triggerState.isActive]);

    const handleChange = useCallback(
        (next: string) => {
            onChange(next);
            detect(next);
        },
        [onChange, detect],
    );

    const applySelection = useCallback(
        (suggestion: MentionSuggestion) => {
            const editable = getEditable();
            const { value: nextValue, caret } = insertMention(
                value,
                triggerState.triggerIndex,
                triggerState.triggerIndex + 1 + triggerState.query.length,
                suggestion.email,
            );
            onChange(nextValue);
            close();
            // The value change rewrites the editable's content on the next
            // render; restore the caret just after the inserted email.
            if (editable) {
                requestAnimationFrame(() => setCaretOffset(editable, caret));
            }
        },
        [value, triggerState.triggerIndex, triggerState.query, onChange, close, getEditable],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex((i) => (filtered.length ? (i + 1) % filtered.length : 0));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex((i) =>
                        filtered.length ? (i - 1 + filtered.length) % filtered.length : 0,
                    );
                    break;
                case 'Enter':
                case 'Tab':
                    if (filtered.length) {
                        e.preventDefault();
                        applySelection(filtered[selectedIndex]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    close();
                    break;
                case ' ':
                    // Space accepts whatever is typed as-is and dismisses the
                    // dropdown; the character itself still lands in the text.
                    close();
                    break;
                default:
                    break;
            }
        },
        [isOpen, filtered, selectedIndex, applySelection, close],
    );

    // Re-evaluate the trigger on caret moves that aren't text changes (arrow
    // keys, clicks). Keystrokes that drive the dropdown are handled above.
    const handleSelect = useCallback(() => detect(value), [detect, value]);

    // Clicking a dropdown item calls preventDefault on mousedown so focus never
    // leaves the editable; a real blur therefore means a click-outside — close.
    const handleBlur = useCallback(() => close(), [close]);

    return (
        <MentionContainer
            ref={containerRef}
            onKeyDown={handleKeyDown}
            onKeyUp={handleSelect}
            onMouseUp={handleSelect}
            onBlur={handleBlur}
            data-testid={dataTestId ? `${dataTestId}-mention-container` : undefined}
        >
            <LinkifyTextarea
                id={id}
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                hasError={hasError}
                disabled={disabled}
                spellCheck={spellCheck}
                data-testid={dataTestId}
            />
            {isOpen && triggerState.caretRect ? (
                <MentionDropdown
                    suggestions={filtered}
                    caretRect={triggerState.caretRect}
                    selectedIndex={selectedIndex}
                    onSelect={applySelection}
                    onHover={setSelectedIndex}
                />
            ) : null}
        </MentionContainer>
    );
};
